import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  ShieldAlert,
  ChevronRight,
} from "lucide-react";
import { api } from "../lib/api";
import SeoHead from "../components/SeoHead";
import ReadingProgressBar from "../components/ReadingProgressBar";
import WatchButton from "../components/WatchButton";
import { RelatedGuides } from "../components/RelatedGuides";
import { splitBrandAndName } from "../lib/productDisplay";

const VERDICT_VISUAL = {
  HIGH: {
    chip: "bg-rose-100 text-rose-800 border-rose-200",
    Icon: AlertCircle,
    label: "Avoid",
  },
  MODERATE: {
    chip: "bg-amber-100 text-amber-900 border-amber-200",
    Icon: AlertTriangle,
    label: "Caution",
  },
  LOW: {
    chip: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Icon: CheckCircle2,
    label: "No label conflict found",
  },
};

function Skeleton() {
  return (
    <main className="bg-cream-50 min-h-[80vh]" data-testid="ingredient-page-skeleton">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 space-y-6 animate-pulse">
        <div className="h-3 w-1/4 bg-cream-200 rounded" />
        <div className="h-12 w-3/4 bg-cream-200 rounded" />
        <div className="h-8 w-32 bg-cream-200 rounded-full" />
        <div className="h-32 w-full bg-cream-200 rounded-2xl" />
        <div className="h-40 w-full bg-cream-200 rounded-2xl" />
      </div>
    </main>
  );
}

function NotFound({ slug }) {
  return (
    <main className="bg-cream-50 min-h-[80vh] flex items-center justify-center px-5">
      <SeoHead
        title="Ingredient not found — Blendsafe"
        description="We don't have data on this ingredient yet. Browse the catalog or run a check."
        canonicalPath={`/ingredients/${slug}`}
      />
      <div
        data-testid="ingredient-page-not-found"
        className="max-w-md text-center space-y-5"
      >
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-[-0.02em] text-ink">
          We don't have a page for that ingredient yet.
        </h1>
        <p className="text-ink-soft">
          Run a personalized check to see how it interacts with your medications
          and the rest of your routine.
        </p>
        <Link
          to="/check"
          data-testid="ingredient-page-cta-not-found"
          className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Check your routine <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </main>
  );
}

function ProductTile({ p }) {
  const { brand, name } = splitBrandAndName(p);
  return (
    <Link
      to={`/products/${p.slug}`}
      data-testid={`ingredient-page-product-${p.id}`}
      className="group flex items-center gap-3 rounded-2xl bg-white border border-surface-line p-3 hover:shadow-sm transition-all hover:border-ink/20"
    >
      <div className="w-12 h-12 shrink-0 rounded-xl bg-cream-100 border border-surface-line overflow-hidden flex items-center justify-center">
        {p.image_url ? (
          <img
            src={p.image_url}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <span className="text-[12px] font-bold text-ink-mute uppercase tracking-wide">
            {(brand || name || "?").charAt(0)}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {brand && (
          <div className="text-[10.5px] uppercase tracking-[0.16em] font-medium text-ink-mute truncate">
            {brand}
          </div>
        )}
        <div className="text-[14px] font-semibold text-ink truncate">{name}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-ink-light shrink-0 group-hover:text-ink transition-colors" />
    </Link>
  );
}

function IngredientView({ data, onAuthNeeded }) {
  const {
    id: ingredientId,
    name,
    category,
    description,
    commonly_found_in,
    who_should_use = [],
    who_should_be_cautious = [],
    common_conflicts = [],
    products = [],
    meta = {},
    slug,
  } = data;

  return (
    <main className="bg-cream-50 min-h-[80vh]" data-testid="ingredient-page">
      <ReadingProgressBar />
      <SeoHead
        title={meta.title}
        description={meta.description}
        canonicalPath={`/ingredients/${slug}`}
      />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14 space-y-8">
        {/* Hero */}
        <header className="space-y-4">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-ink-mute">
            Skincare ingredient
          </p>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <h1
              data-testid="ingredient-page-h1"
              className="text-3xl sm:text-5xl font-extrabold tracking-[-0.02em] text-ink leading-[1.05] flex-1 min-w-0"
            >
              {name}
            </h1>
            <WatchButton
              kind="ingredient"
              targetId={ingredientId}
              labelHint={name}
              onAuthNeeded={onAuthNeeded}
              className="mt-2"
            />
          </div>
          {category && (
            <span
              data-testid="ingredient-page-category"
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-ink text-cream-50 text-[12px] font-bold uppercase tracking-[0.14em]"
            >
              <Sparkles className="w-3.5 h-3.5" /> {category}
            </span>
          )}
        </header>

        {/* What it does */}
        {description && (
          <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-3">
            <h2 className="text-lg font-bold text-ink">What it does</h2>
            <p className="text-ink-soft leading-relaxed text-[15px]">{description}</p>
            {commonly_found_in && (
              <p className="text-[13px] text-ink-mute pt-2 border-t border-surface-line">
                <span className="font-semibold">Commonly found in:</span> {commonly_found_in}
              </p>
            )}
          </section>
        )}

        {/* Who should use it */}
        {who_should_use.length > 0 && (
          <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-4">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <h2 className="text-lg font-bold text-ink">Who should use it</h2>
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">
                Good fit
              </span>
            </div>
            <ul className="space-y-3" data-testid="ingredient-page-use">
              {who_should_use.map((a, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 mt-1 text-emerald-600 shrink-0" />
                  <div className="text-[14px] leading-relaxed">
                    <span className="font-bold text-ink">{a.label}</span>
                    {a.note && <span className="text-ink-soft"> — {a.note}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Who should be cautious */}
        {who_should_be_cautious.length > 0 && (
          <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-4">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <h2 className="text-lg font-bold text-ink">Who should be cautious</h2>
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-800">
                Care needed
              </span>
            </div>
            <ul className="space-y-3" data-testid="ingredient-page-cautious">
              {who_should_be_cautious.map((a, i) => (
                <li key={i} className="flex items-start gap-3">
                  <ShieldAlert className="w-4 h-4 mt-1 text-amber-600 shrink-0" />
                  <div className="text-[14px] leading-relaxed">
                    <span className="font-bold text-ink">{a.label}</span>
                    {a.note && <span className="text-ink-soft"> — {a.note}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Common conflicts */}
        {common_conflicts.length > 0 && (
          <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-4">
            <h2 className="text-lg font-bold text-ink">Common conflicts &amp; pairings</h2>
            <p className="text-[13px] text-ink-soft">
              Click any pairing to see the full compatibility report.
            </p>
            <ul className="space-y-3" data-testid="ingredient-page-conflicts">
              {common_conflicts.map((c, i) => {
                const v = VERDICT_VISUAL[c.verdict] || VERDICT_VISUAL.MODERATE;
                const Icon = v.Icon;
                return (
                  <li key={i}>
                    <Link
                      to={c.pair_url}
                      data-testid={`ingredient-page-conflict-${c.other_slug}`}
                      className="flex items-start gap-3 pb-3 last:pb-0 border-b last:border-b-0 border-surface-line group"
                    >
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 h-6 mt-0.5 rounded-full border font-bold text-[11px] uppercase tracking-wide shrink-0 ${v.chip}`}
                      >
                        <Icon className="w-3 h-3" /> {v.label}
                      </span>
                      <div className="text-[14px] leading-relaxed flex-1">
                        <span className="font-bold text-ink group-hover:underline underline-offset-4">
                          {name} + {c.other_name}
                        </span>
                        {c.summary && (
                          <span className="text-ink-soft"> — {c.summary}</span>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-ink-light shrink-0 mt-1 group-hover:text-ink transition-colors" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* Products containing this ingredient */}
        {products.length > 0 && (
          <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-4">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <h2 className="text-lg font-bold text-ink">
                Products with {name}
              </h2>
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-mute">
                {products.length} in catalog
              </span>
            </div>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              data-testid="ingredient-page-products"
            >
              {products.map((p) => (
                <ProductTile key={p.id} p={p} />
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <Link
            to="/check"
            data-testid="ingredient-page-cta"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Check {name} against your medications <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/routine"
            className="text-sm font-semibold text-ink-soft underline decoration-from-font underline-offset-4 hover:text-ink"
          >
            or check your full routine
          </Link>
        </div>

        <p className="text-[11px] text-ink-mute pt-4 border-t border-surface-line leading-relaxed">
          Educational content — not medical advice. Compatibility depends on
          formulation, concentration, and individual skin tolerance.
        </p>

        <RelatedGuides anchorType="ingredient" anchorId={ingredientId} />
      </div>
    </main>
  );
}

export default function IngredientPage({ initialData, onAuthNeeded }) {
  const params = useParams();
  const slug = params.slug || params.combined;
  const [data, setData] = useState(initialData || null);
  const [status, setStatus] = useState(initialData ? "ok" : "loading");

  useEffect(() => {
    if (initialData) {
      setData(initialData);
      setStatus("ok");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    api
      .get(`/seo/ingredient/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        setStatus("ok");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("notfound");
      });
    return () => {
      cancelled = true;
    };
  }, [slug, initialData]);

  if (status === "loading") return <Skeleton />;
  if (status === "notfound") return <NotFound slug={slug} />;
  return <IngredientView data={data} onAuthNeeded={onAuthNeeded} />;
}
